import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import HomepageAuth from "./HomepageAuth";
import FoundPetModal from "./FoundPetModal";
import RegisterPetPage from "../pages/RegisterPetPage";
import { CheckCircle, ArrowLeft } from "lucide-react";

const EnhancedModal = ({ modalState, onClose, onNextStep, user }) => {
  const { themeClasses } = useTheme();
  const navigate = useNavigate();

  // Auto-advance to task step when user is authenticated
  useEffect(() => {
    console.log("EnhancedModal useEffect:", {
      user: !!user,
      step: modalState.step,
    });
    if (user && modalState.step === "auth") {
      console.log("EnhancedModal: User authenticated, advancing to next step");
      onNextStep();
    }
  }, [user, modalState.step, onNextStep]);

  // Handle task completion
  const handleTaskComplete = () => {
    onNextStep();
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [onClose]);

  const renderContent = () => {
    switch (modalState.step) {
      case "auth":
        return (
          <HomepageAuth
            onClose={onClose}
            onSuccess={() => {
              console.log(
                "EnhancedModal: onSuccess called, advancing to next step"
              );
              onNextStep();
            }}
            mode={modalState.mode}
            title={
              modalState.mode === "found-pet"
                ? "Sign in to Report Found Pet"
                : "Sign in to Register Pet"
            }
          />
        );

      case "task":
        // FoundPetModal is handled separately above
        return (
          <div className="h-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2
                className={`text-xl font-semibold ${themeClasses.text.primary}`}
              >
                Register Your Pet
              </h2>
              <button
                onClick={onClose}
                className={`${themeClasses.text.secondary} hover:${themeClasses.text.primary} p-2 rounded-full transition-colors`}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div
              className="p-4 overflow-y-auto"
              style={{ maxHeight: "calc(90vh - 120px)" }}
            >
              <RegisterPetPage onComplete={handleTaskComplete} isModal={true} />
            </div>
          </div>
        );

      case "success":
        return (
          <div className="text-center p-8">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
            </div>
            <h2
              className={`text-2xl font-bold ${themeClasses.text.primary} mb-4`}
            >
              {modalState.mode === "found-pet"
                ? "Found Pet Reported!"
                : "Pet Registered Successfully!"}
            </h2>
            <p className={`${themeClasses.text.secondary} mb-6`}>
              {modalState.mode === "found-pet"
                ? "Thank you for helping reunite pets with their families. We'll notify you if there are any matches."
                : "Your pet has been successfully registered. You can now access all features from your dashboard."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  onClose();
                  navigate("/dashboard");
                }}
                className={`${themeClasses.bg.button} text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity`}
              >
                Go to Dashboard
              </button>
              <button
                onClick={onClose}
                className={`${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} px-6 py-3 rounded-lg font-medium border ${themeClasses.border.primary} hover:${themeClasses.border.accent} transition-colors`}
              >
                Stay on Homepage
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Handle FoundPetModal separately since it's a complete modal
  if (modalState.mode === "found-pet" && modalState.step === "task") {
    console.log("EnhancedModal: Rendering FoundPetModal");
    return <FoundPetModal isOpen={true} onClose={onClose} user={user} />;
  }

  console.log(
    "EnhancedModal: Rendering regular modal content, step:",
    modalState.step,
    "mode:",
    modalState.mode
  );

  return (
    <>
      {modalState.step === "auth" ? (
        renderContent()
      ) : (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleBackdropClick}
        >
          <div
            className={`${themeClasses.bg.card} border ${themeClasses.border.primary} rounded-2xl w-full max-w-4xl max-h-[90vh] shadow-2xl relative overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            {renderContent()}
          </div>
        </div>
      )}
    </>
  );
};

export default EnhancedModal;
